// Theme system index - centralized theme exports and utilities
export { default as lightTheme } from './lightTheme';
export { default as darkTheme } from './darkTheme';
export * from './tokens';

// Theme utility functions
export const getThemeColors = (theme) => {
  return theme.custom?.colors || {};
};

// Helper function to get component-specific colors
export const getChatSessionColors = (theme) => {
  const colors = getThemeColors(theme);
  return colors.chatSession || {};
};

export const getProjectColors = (theme) => {
  const colors = getThemeColors(theme);
  return colors.project || {};
};

export const getMessageFontColors = (theme) => {
  const colors = getThemeColors(theme);
  return colors.messageFont || {};
};

// Helper functions to replace hardcoded theme mode checks
export const isDarkMode = (theme) => {
  return theme.palette.mode === 'dark';
};

export const isLightMode = (theme) => {
  return theme.palette.mode === 'light';
};

// Background color helpers
export const getBackgroundColor = (theme, variant = 'default') => {
  const colors = getThemeColors(theme);
  return colors.background?.[variant] || theme.palette.background.default;
};

// Border color helpers
export const getBorderColor = (theme, variant = 'default') => {
  const colors = getThemeColors(theme);
  return colors.border?.[variant] || theme.palette.divider;
};

// Interactive state helpers
export const getHoverColor = (theme, component = null) => {
  const colors = getThemeColors(theme);
  if (component && colors.action?.[component]) {
    return colors.action[component].hover;
  }
  return colors.action?.hover || theme.palette.action.hover;
};

export const getTranslucentColor = (theme, opacity = 'base') => {
  const colors = getThemeColors(theme);
  return colors.action?.translucent?.[opacity] || theme.palette.action.hover;
};

// Text color helpers
export const getTextColor = (theme, variant = 'primary') => {
  const colors = getThemeColors(theme);
  return colors.text?.[variant] || theme.palette.text.primary;
};

// Elevation/shadow helpers
export const getElevation = (theme, level = 'base') => {
  return theme.custom?.elevation?.[level] || 'none';
};

// Animation helpers
export const getAnimationDuration = (theme, speed = 'normal') => {
  return theme.custom?.animation?.duration?.[speed] || '0.3s';
};

export const getAnimationEasing = (theme, type = 'ease') => {
  return theme.custom?.animation?.easing?.[type] || 'ease';
};

// Utility to create theme-aware styles
export const createThemeStyles = (lightStyles, darkStyles) => {
  return (theme) => {
    return isDarkMode(theme) ? darkStyles : lightStyles;
  };
};

// Component-specific style helpers
export const createChatSessionStyles = (theme) => {
  const chatColors = getChatSessionColors(theme);
  return {
    default: {
      backgroundColor: chatColors.background?.default || 'transparent',
      color: chatColors.text?.default || theme.palette.text.primary,
    },
    active: {
      backgroundColor: chatColors.background?.active || theme.palette.action.selected,
      color: chatColors.text?.active || theme.palette.primary.main,
    },
    hover: {
      backgroundColor: chatColors.background?.hover || theme.palette.action.hover,
    },
    activeHover: {
      backgroundColor: chatColors.background?.activeHover || theme.palette.action.selected,
    },
  };
};

export const createMenuButtonStyles = (theme) => {
  const colors = getThemeColors(theme);
  const menuColors = colors.action?.menuButton || {};
  
  return {
    background: menuColors.background || getTranslucentColor(theme, 'base'),
    hover: menuColors.hover || getTranslucentColor(theme, 'md'),
    border: menuColors.border || theme.palette.primary.main,
  };
};

export const createProjectItemStyles = (theme) => {
  const colors = getThemeColors(theme);
  const projectColors = colors.action?.projectItem || {};
  
  return {
    background: projectColors.background || getTranslucentColor(theme, 'xs'),
    hover: projectColors.hover || getTranslucentColor(theme, 'base'),
    border: projectColors.border || getBorderColor(theme),
  };
}; 